import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { UserCog } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// "Assigned to" picker on a case. Changing it hands the case over: open tasks + client move too,
// a note is logged on the case and both staff members are notified (done server-side).
export function CaseReassign({ app, onDone }) {
  const [staff, setStaff] = useState([]);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const current = app.assigned_staff || app.assigned_consultant || "";

  useEffect(() => {
    api.get("/staff").then((r) => setStaff((r.data || []).filter((s) => s.active !== false))).catch(() => {});
  }, []);

  const nameOf = (id) => staff.find((s) => s.id === id)?.name || "Unassigned";

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/applications/${app.id}`, { assigned_to: pick || null });
      toast.success(pick ? `Case reassigned to ${nameOf(pick)}` : "Case unassigned");
      setPick(null);
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 text-sm" title="Reassign this case to another staff member">
        <UserCog className="h-4 w-4 text-slate-400" />
        <select data-testid="case-reassign-select" value={current} onChange={(e) => { if (e.target.value !== current) setPick(e.target.value); }}
          className="bg-transparent font-medium text-slate-700 focus:outline-none">
          <option value="">Unassigned</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </label>
      <Dialog open={pick !== null} onOpenChange={(o) => { if (!o && !busy) setPick(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="case-reassign-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" /> Reassign this case?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p><b>{nameOf(current)}</b> → <b>{pick ? nameOf(pick) : "Unassigned"}</b></p>
            {pick && current && <ul className="list-disc pl-5 text-xs text-slate-500 space-y-1">
              <li>{`${nameOf(current)}'s`} open tasks on this case move to {nameOf(pick)}.</li>
              <li>The client is also assigned to {nameOf(pick)}.</li>
              <li>The case leaves {`${nameOf(current)}'s`} active list and dashboard.</li>
              <li>A note is added to the case and both are notified.</li>
            </ul>}
          </div>
          <div className="flex gap-2">
            <button data-testid="case-reassign-cancel" onClick={() => setPick(null)} disabled={busy} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold">Cancel</button>
            <button data-testid="case-reassign-confirm" onClick={save} disabled={busy} className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">{busy ? "Reassigning…" : "Reassign"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
